import {GitCommitIcon} from '@primer/octicons-react'
import {graphql} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import {LABELS} from '../constants/labels'
import {createIssueEventExternalUrl} from '../utils/urls'
import type {ReferencedEvent$key} from './__generated__/ReferencedEvent.graphql'
import styles from './ReferencedEvent.module.css'
import {ReferencedEventInner, type ReferencedEventInnerProps} from './ReferencedEventInner'
import {Ago} from './row/Ago'
import {TimelineRow} from './row/TimelineRow'
import type {PreloadedSecondaryQueryType} from './secondary-timeline-query'

type ReferencedEventProps = {
  queryRef: ReferencedEvent$key
  rollupGroup?: Record<string, ReferencedEvent$key[]>
  issueUrl: string
  onLinkClick?: (event: MouseEvent) => void
  highlightedEventId?: string
  refAttribute?: React.MutableRefObject<HTMLDivElement | null>
  viewer: string | null
  secondaryTimelineQueryRef?: PreloadedSecondaryQueryType
}

const ReferencedEventFragment = graphql`
  fragment ReferencedEvent on ReferencedEvent {
    id
    databaseId
    willCloseSubject
    subject {
      __typename
    }
    actor {
      ...TimelineRowEventActor
    }
    commit {
      ...ReferencedEventInner
    }
    createdAt
  }
`

export function ReferencedEvent({
  queryRef,
  issueUrl,
  onLinkClick,
  highlightedEventId,
  refAttribute,
  rollupGroup,
  viewer,
  secondaryTimelineQueryRef,
}: ReferencedEventProps): React.ReactElement {
  const {actor, createdAt, commit, databaseId, subject, willCloseSubject, id} = useFragment(
    ReferencedEventFragment,
    queryRef,
  )

  if (commit === null) {
    return <></>
  }

  const highlighted = String(databaseId) === highlightedEventId
  const rolledUpGroup = rollupGroup && rollupGroup['ReferencedEvent'] ? rollupGroup['ReferencedEvent'] : [queryRef]
  const numItems = rolledUpGroup.length

  return (
    <TimelineRow
      highlighted={highlighted}
      refAttribute={refAttribute}
      actor={actor}
      createdAt={createdAt}
      deepLinkUrl={issueUrl}
      onLinkClick={onLinkClick}
      showAgoTimestamp={false}
      leadingIcon={GitCommitIcon}
    >
      <TimelineRow.Main>
        {`${LABELS.timeline.addedCommitsThatReferences(numItems)} `}
        <Ago timestamp={new Date(createdAt)} linkUrl={createIssueEventExternalUrl(issueUrl, databaseId)} />
        <div className={styles.Box}>
          {rolledUpGroup.map((itemQueryRef, index) => (
            <ReferencedItem
              // eslint-disable-next-line @eslint-react/no-array-index-key
              key={`${databaseId}_${index}`}
              itemQueryRef={itemQueryRef}
              subjectType={subject.__typename}
              viewerLogin={viewer}
              eventId={id}
              willCloseSubject={willCloseSubject}
              secondaryTimelineQueryRef={secondaryTimelineQueryRef}
            />
          ))}
        </div>
      </TimelineRow.Main>
    </TimelineRow>
  )
}

function ReferencedItem({
  itemQueryRef,
  ...props
}: {itemQueryRef: ReferencedEvent$key} & Omit<ReferencedEventInnerProps, 'commitKey'>) {
  const {commit} = useFragment(ReferencedEventFragment, itemQueryRef)
  if (!commit) return null

  return <ReferencedEventInner commitKey={commit} {...props} />
}

try{ ReferencedEvent.displayName ||= 'ReferencedEvent' } catch {}
try{ ReferencedItem.displayName ||= 'ReferencedItem' } catch {}